/**
 * Order lifecycle state machine — single source of truth.
 *
 * The order status moves forward through `LIFECYCLE_ORDER`. The audit
 * found three independent call sites enforcing "advance forward only" with
 * inline conditionals, and `updateOrder` doing no check at all — which
 * meant an admin form could move an order from `IN_PRODUCTION` back to
 * `OPEN`. This module centralizes the rules so every writer enforces the
 * same contract.
 *
 * See root CLAUDE.md "Protected Zones #7" for the documented invariant.
 */

import type { OrderStatus } from '../../../types';

/**
 * Strict forward-only order. Indexes are meaningful: `i < j` means status `j`
 * is later in the lifecycle than status `i`.
 */
export const LIFECYCLE_ORDER: readonly OrderStatus[] = [
  'AWAITING_DEPOSIT',
  'CONFIRMED',
  'MEASURING',
  'IN_PRODUCTION',
  'SHIPPED',
  'RECEIVED',
  'READY_FOR_DELIVERY',
  'DELIVERED',
  'COMPLETED',
] as const;

/**
 * Statuses that exist outside the forward order. Admin can put an order
 * into any of these at any time (e.g. customer goes silent → `ON_HOLD`),
 * but an order in one of these states can only move into another terminal,
 * never re-enter the forward order. The lifecycle is intentionally
 * one-directional — a "resumed" order is a new order, not a re-opened one.
 */
export const MANUAL_TERMINAL_STATUSES: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
  'CANCELLED',
  'ON_HOLD',
]);

/** Position in `LIFECYCLE_ORDER`. Returns -1 for terminal statuses. */
export const indexOfStatus = (s: OrderStatus): number => LIFECYCLE_ORDER.indexOf(s);

/**
 * True when transitioning from `from` to `to` is legal:
 *  - Same status (a no-op write is harmless).
 *  - `to` is a manual-terminal status (admin escape hatch).
 *  - Both `from` and `to` are in the forward order AND `to` is strictly later.
 *
 * False for:
 *  - Backward moves inside the forward order (e.g. `MEASURING` → `OPEN`).
 *  - Re-entering the forward order from a terminal (e.g. `CANCELLED` → `OPEN`).
 *  - Any status not in the forward order or the terminal set (shouldn't happen
 *    with the current type, but the helper is defensive).
 */
export const canTransition = (from: OrderStatus, to: OrderStatus): boolean => {
  if (from === to) return true;
  if (MANUAL_TERMINAL_STATUSES.has(to)) return true;
  if (MANUAL_TERMINAL_STATUSES.has(from)) return false;
  const fi = indexOfStatus(from);
  const ti = indexOfStatus(to);
  return fi >= 0 && ti >= 0 && ti > fi;
};

/**
 * Strictly-forward variant of `canTransition`. Used by auto-advance helpers
 * that should never sidestep into a terminal status (deposit-paid should
 * never auto-cancel an order, etc.). Same-status counts as not-forward
 * — auto-advance is idempotent and shouldn't write unnecessarily.
 */
export const isForward = (from: OrderStatus, to: OrderStatus): boolean => {
  if (from === to) return false;
  if (MANUAL_TERMINAL_STATUSES.has(from) || MANUAL_TERMINAL_STATUSES.has(to)) return false;
  const fi = indexOfStatus(from);
  const ti = indexOfStatus(to);
  return fi >= 0 && ti >= 0 && ti > fi;
};
